import type { AIModel } from "@/types";

export type LocalWebLlmModelInput = string | Partial<AIModel>;

export type ResolvedLocalWebLlmModel = {
  modelId: string;
  engineModelId: string;
  modelUrl: string;
  modelLibCandidates: string[];
  contextWindowSize?: number;
};

export type ResolveLocalWebLlmModelOptions = {
  preferF32?: boolean;
};

const isLocalDevRuntime =
  typeof window !== "undefined" && /^http:\/\/localhost:8080$/i.test(window.location.origin);
const browserOrigin = typeof window !== "undefined" ? window.location.origin : "";

export const MODEL_BASE_URL = "https://models2.mapleos.ai/mlc-ai";
export const WEB_LLM_BASE_URL = isLocalDevRuntime
  ? `${browserOrigin}/web-llm-models/web-llm-models/v0_2_80`
  : "https://mapleos.ai/web-llm-models/web-llm-models/v0_2_80";

const FALLBACK_CONTEXT = 4096;

function uniq(values: Array<string | undefined | null>): string[] {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value))));
}

function replaceQuantizationVariant(value: string, targetVariant: "q4f16_1" | "q4f32_1") {
  return value
    .replace(/q4f16_1/gi, targetVariant)
    .replace(/q4f32_1/gi, targetVariant);
}

function getRawModelIdentifier(input: LocalWebLlmModelInput): string {
  return typeof input === "string"
    ? input
    : input.apiName || input.id || "Llama-3.2-1B-Instruct-q4f32_1-MLC";
}

function getEngineModelId(input: LocalWebLlmModelInput, overrideModelId?: string): string {
  return String(overrideModelId || getRawModelIdentifier(input))
    .trim()
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")
    .replace(/\/+$/, "") || "Llama-3.2-1B-Instruct-q4f32_1-MLC";
}

export function normalizeWebLlmModelId(input: LocalWebLlmModelInput): string {
  const raw = getRawModelIdentifier(input);

  return String(raw)
    .trim()
    .replace(/\\/g, "/")
    .split("/")
    .filter(Boolean)
    .pop() || "Llama-3.2-1B-Instruct-q4f32_1-MLC";
}

function stripMlcSuffix(modelId: string): string {
  return modelId.replace(/-MLC$/i, "");
}

function ensureTrailingSlash(value: string) {
  return value.endsWith("/") ? value : `${value}/`;
}

function tryResolveAbsoluteUrl(value: string): string | null {
  try {
    return new URL(value).href;
  } catch {
    return null;
  }
}

function resolveModelUrl(input: LocalWebLlmModelInput, effectiveModelId: string, options?: ResolveLocalWebLlmModelOptions): string {
  if (typeof input !== "string") {
    const explicit = [input.served_url, input.hf_compat_url, input.huggingface_url]
      .find((value) => typeof value === "string" && value.trim().length > 0);

    if (explicit) {
      const explicitValue = options?.preferF32
        ? replaceQuantizationVariant(explicit.trim(), "q4f32_1")
        : explicit.trim();
      const resolved = tryResolveAbsoluteUrl(explicitValue);
      if (resolved) {
        return ensureTrailingSlash(resolved);
      }
    }
  }

  return `${MODEL_BASE_URL}/${effectiveModelId}`;
}

function inferContextWindowSize(input: LocalWebLlmModelInput, normalizedModelId: string): number {
  const explicit = typeof input === "string" ? undefined : input.context_length;
  if (typeof explicit === "number" && explicit > 0) {
    return explicit;
  }

  if (/Hermes-3-Llama-3\.1-8B/i.test(normalizedModelId)) {
    return 131072;
  }

  if (/Hermes-2-Pro-Llama-3-8B/i.test(normalizedModelId)) {
    return 8192;
  }

  if (/snowflake-arctic-embed/i.test(normalizedModelId)) {
    return 512;
  }

  if (/^(TinyLlama|RedPajama|phi-1_5|phi-2)/i.test(normalizedModelId)) {
    return 2048;
  }

  return FALLBACK_CONTEXT;
}

function formatCtxLabel(contextWindowSize: number): string {
  if (contextWindowSize >= 1024 && contextWindowSize % 1024 === 0) {
    return `ctx${Math.round(contextWindowSize / 1024)}k`;
  }

  return `ctx${contextWindowSize}`;
}

function inferContextLabels(normalizedModelId: string, contextWindowSize: number): string[] {
  if (/snowflake-arctic-embed/i.test(normalizedModelId)) {
    return ["ctx512"];
  }

  if (/Hermes-3-Llama-3\.1-8B/i.test(normalizedModelId)) {
    return uniq([formatCtxLabel(contextWindowSize), "ctx128k", "ctx8k", "ctx4k"]);
  }

  if (/Hermes-2-Pro-Llama-3-8B/i.test(normalizedModelId)) {
    return uniq([formatCtxLabel(contextWindowSize), "ctx8k", "ctx4k"]);
  }

  if (/Hermes-2-Pro-Mistral-7B/i.test(normalizedModelId)) {
    return ["ctx4k"];
  }

  return [formatCtxLabel(contextWindowSize)];
}

function inferChunkLabels(normalizedModelId: string): string[] {
  if (/snowflake-arctic-embed/i.test(normalizedModelId)) {
    return ["cs512_batch32"];
  }

  if (/Hermes/i.test(normalizedModelId) || /vision/i.test(normalizedModelId)) {
    return ["cs2k", "cs1k"];
  }

  return ["cs1k"];
}

function mapModelLibId(baseId: string): string {
  return baseId
    .replace(/^DeepSeek-R1-Distill-Qwen-1\.5B/i, "Qwen2-1.5B-Instruct")
    .replace(/^DeepSeek-R1-Distill-Qwen-7B/i, "Qwen2-7B-Instruct")
    .replace(/^Qwen2\.5-(?:Coder-|Math-)?0\.5B-Instruct/i, "Qwen2-0.5B-Instruct")
    .replace(/^Qwen2\.5-(?:Coder-|Math-)?1\.5B-Instruct/i, "Qwen2-1.5B-Instruct")
    .replace(/^Qwen2\.5-(?:Coder-|Math-)?7B-Instruct/i, "Qwen2-7B-Instruct")
    .replace(/^Hermes-3-Llama-3\.1-8B/i, "Llama-3.1-8B-Instruct")
    .replace(/^Hermes-3-Llama-3\.2-3B/i, "Llama-3.2-3B-Instruct")
    .replace(/^Hermes-2-Pro-Llama-3-8B/i, "Llama-3-8B-Instruct");
}

function getModelLibBaseIds(baseId: string): string[] {
  if (/^Hermes-2-Pro-Mistral-7B/i.test(baseId)) {
    return uniq([
      baseId,
      baseId.replace(/^Hermes-2-Pro-Mistral-7B/i, "Mistral-7B-Instruct-v0.3"),
      baseId.replace(/^Hermes-2-Pro-Mistral-7B/i, "Mistral-7B-Instruct-v0.2"),
    ]);
  }

  return uniq([mapModelLibId(baseId), baseId]);
}

function buildModelLibCandidates(effectiveModelId: string, contextWindowSize: number, options?: ResolveLocalWebLlmModelOptions) {
  const baseId = stripMlcSuffix(effectiveModelId);
  const ctxLabels = inferContextLabels(effectiveModelId, contextWindowSize);
  const chunkLabels = inferChunkLabels(effectiveModelId);
  const preferredVariant = options?.preferF32 ? "q4f32_1" : null;
  const fallbackVariant = preferredVariant === "q4f32_1" ? "q4f16_1" : "q4f32_1";

  const preferredBaseIds = getModelLibBaseIds(preferredVariant ? replaceQuantizationVariant(baseId, preferredVariant) : baseId);
  const fallbackBaseIds = getModelLibBaseIds(replaceQuantizationVariant(baseId, fallbackVariant));

  return uniq(
    [preferredBaseIds, fallbackBaseIds].flatMap((baseIds) =>
      baseIds.flatMap((candidateBaseId) =>
        ctxLabels.flatMap((ctxLabel) =>
          chunkLabels.map(
            (chunkLabel) => `${WEB_LLM_BASE_URL}/${candidateBaseId}-${ctxLabel}_${chunkLabel}-webgpu.wasm`
          )
        )
      )
    )
  );
}

export function resolveLocalWebLlmModel(input: LocalWebLlmModelInput, options?: ResolveLocalWebLlmModelOptions): ResolvedLocalWebLlmModel {
  const requestedModelId = normalizeWebLlmModelId(input);
  const modelId = options?.preferF32
    ? replaceQuantizationVariant(requestedModelId, "q4f32_1")
    : requestedModelId;
  const engineModelId = getEngineModelId(input, modelId);
  const contextWindowSize = inferContextWindowSize(input, modelId);
  const modelUrl = resolveModelUrl(input, modelId, options);

  return {
    modelId,
    engineModelId,
    modelUrl,
    modelLibCandidates: buildModelLibCandidates(modelId, contextWindowSize, options),
    contextWindowSize,
  };
}